import { apiClient, getPlatformUrl } from "../../lib/api";

export class DataManagerGateway {
  async fetchInfo(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/stored/info`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchIdCode(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/subjects/info`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async addIdCode(): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/service`;
    const res = await apiClient.post({ url: url });
    return res as any;
  }

  async editIdCode(req: any): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/service/category`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async deleteIdCode(req: any): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/service`;
    const res = await apiClient.remove({ url: url, body: req });
    return res as any;
  }

  async fetchSummary(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/summary`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchNode(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/data_properties`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async editNode(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/data_properties`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async fetchDiscretize(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/discretization`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async editDiscretize(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/discretization`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async fetchNodeHistogram(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/node/histogram`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async fetchGroup(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/subjects/info`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async addGroup(req: any): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/category`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async editGroup(req: any): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/category`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async deleteGroup(req: any): Promise<any> {
    const url = `${process.env.REACT_APP_XCAUSAL_SERVICE_URL}/api/vw/vendor/category`;
    const res = await apiClient.remove({ url: url, body: req });
    return res as any;
  }

  // 直近アップロードの進行状況（ベンダー単位）。
  // ノード設定・因果推論データ設定が未完了かどうかの判定に使う。
  async fetchCsvStorageStatus(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/csv-storage-status`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async checkFile(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/check`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async uploadFile(req: any): Promise<any> {
    const query = `store_type=${encodeURIComponent(req.store_type)}`;
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/upload?${query}`;
    const body = new FormData();
    req.files.forEach((file: any) => body.append("files", file));
    const res = await apiClient.upload({ url: url, body: body });
    return res as any;
  }

  async storeFile(req: any): Promise<any> {
    const query = `consent_type=${encodeURIComponent(req.consent_type)}`;
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/store?${query}`;
    const res = await apiClient.post({ url: url, body: req.data_ids });
    return res as any;
  }

  // ===== 2 フェーズ取込 (presigned + API-1/API-2) =====

  async issueUploadUrl(req: any): Promise<any> {
    const query = `store_type=${encodeURIComponent(req.store_type)}`;
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/upload_url?${query}`;
    // 1 リクエストで N ファイル分の presigned URL を発行する (レスポンスは同順)。
    const res = await apiClient.post({ url: url, body: { files: req.files } });
    return res as any;
  }

  // S3 presigned PUT は apiClient を使わない (Authorization ヘッダを付けると S3 が拒否する。
  // また body は gz Blob なので JSON 化してはいけない) → 生 fetch で PUT する。
  async putPresigned(req: any): Promise<void> {
    const res = await fetch(req.url, { method: "PUT", body: req.blob });
    if (!res.ok) {
      throw new Error(`presigned PUT failed: ${res.status} ${res.statusText}`);
    }
  }

  async triggerRawIngest(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/raw_ingest`;
    const res = await apiClient.post({ url: url, body: { upload_id: req.upload_id } });
    return res as any;
  }

  async getIngestStatus(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/ingest/${encodeURIComponent(req.upload_id)}`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async confirmAttributes(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/ingest/confirm_attributes`;
    const res = await apiClient.post({
      url: url,
      body: { upload_id: req.upload_id, attribute_names: req.attribute_names },
    });
    return res as any;
  }

  async confirmFinest(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/ingest/confirm_finest`;
    const res = await apiClient.post({
      url: url,
      body: { upload_id: req.upload_id, confirmed_finest: req.confirmed_finest },
    });
    return res as any;
  }

  // 取込の無効化 (旧 /store の拒否に相当)。S3 のソース + 未確定 raw を消して REJECTED にする。
  async cancelIngest(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/ingest/cancel`;
    const res = await apiClient.post({
      url: url,
      body: { upload_id: req.upload_id },
    });
    return res as any;
  }

  // 生成する grain セットを ingest job に保存する (ベンダー設定への適用は承認時 = /derive)。
  async confirmGrains(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/ingest/confirm_grains`;
    const res = await apiClient.post({
      url: url,
      body: { upload_id: req.upload_id, grains: req.grains },
    });
    return res as any;
  }

  async getTimeGrains(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/time_grains`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async setTimeGrains(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/time_grains`;
    const res = await apiClient.update({ url: url, body: { grains: req.grains } });
    return res as any;
  }

  // ベンダーの {vendor}_* テーブルを用意する (冪等。既に在ればサーバ側で即返る)。
  async provisionVendor(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/provision`;
    const res = await apiClient.post({ url: url, body: {} });
    return res as any;
  }

  // ---- 設定変更タブ (取込フロー外) ----

  // 現行データ (current 版) を作った取込 job の状態 + 列統計。取込済でも列統計を返す。
  async getCurrentIngest(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/settings/current_ingest`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  // 層別化属性を変更して現行版の raw から作り直す (新しい版が作られる)。
  async changeAttributes(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/settings/attributes`;
    const res = await apiClient.post({
      url: url,
      body: { attribute_names: req.attribute_names },
    });
    return res as any;
  }

  // 現行ノードの集約方法 (node value → mean/median/max/min/sum)。node_def.downsample_method。
  async getAggregations(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/settings/aggregations`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  // 現行版に生成グレインを追加する (削除・最小粒度の変更は不可)。
  async addTimeGrains(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/settings/grains`;
    const res = await apiClient.post({
      url: url,
      body: { grains: req.grains, aggregations: req.aggregations ?? {} },
    });
    return res as any;
  }

  async triggerDerive(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/derive`;
    const res = await apiClient.post({ url: url, body: { upload_id: req.upload_id } });
    return res as any;
  }

  async fetchGraphsMeta(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/prior-graphs/meta`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async updateGraphStatus(req: any): Promise<any> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/prior-graphs/status?${query}`;
    const res = await apiClient.patch({ url: url });
    return res as any;
  }

  async updateGraphData(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/prior-graphs/upsert-graph`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async fetchExtractedEdges(req: any): Promise<any> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/extraction/edges?${query}`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchSimilarTerms(req: any): Promise<any> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/extraction/targets?${query}`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchExtractEdges(req: any): Promise<any> {
    const query = new URLSearchParams();
    for (const term of req.terms) {
      query.append("terms", term);
    }
    query.append("is_saved", String(req.is_saved));
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/extraction/edges?${query}`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async saveExtractEdges(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/extraction/edges`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async deleteExtractEdges(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/extraction/edges`;
    const res = await apiClient.remove({ url: url, body: req });
    return res as any;
  }

  async fetchNodesAvailable(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/assignment/nodes`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchNodeAssign(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/assignment/edges`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchSimilarNodes(req: any): Promise<any> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/assignment/similar-nodes?${query}`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async saveNodeAssign(req: any): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/assignment/assign-node-pairs`;
    const res = await apiClient.post({ url: url, body: req });
    return res as any;
  }

  async fetchGraphMeta(req: any): Promise<any> {
    const baseUrl = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/prior-graphs/meta`;
    const url = req.graph_id
      ? `${baseUrl}?graph_id=${encodeURIComponent(req.graph_id)}`
      : baseUrl;
    const res = await apiClient.get({ url: url });
    return res as any;
  }

  async fetchEdgeCandidates(): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/v1/data_manager/prior/llm/prior-graphs/edge-candidates`;
    const res = await apiClient.get({ url: url });
    return res as any;
  }
}
